import api, { Tags } from 'ev-api/api';

import { moduleInstanceQuery, moduleInstancesQuery } from './module-instances';
import {
  CdxInterpreterInstance,
  CdxModule,
  CdxModuleInstance,
  CdxModuleInstancesListResponse,
  CdxModuleInstanceWithChannels,
  CdxModulesListResponse,
  CdxProxyEnvelope,
  ListInterpreterInstancesParams,
} from './module-types';
import { modulesByGroupQuery } from './modules';

export const INTERPRETERS_MODULE_GROUP = 'interpreters';

type OwnedInstance = {
  module: CdxModule;
  instance: CdxModuleInstance;
};

const cdxInterpreterInstancesApi = api.injectEndpoints({
  endpoints: builder => ({
    listInterpreterInstances: builder.query<
      CdxInterpreterInstance[],
      ListInterpreterInstancesParams
    >({
      // One proxied call at a time on purpose: CDX authenticates each one by
      // calling back into core, so every call in flight holds two core puma
      // threads and a parallel fan-out can exhaust them.
      queryFn: async (
        { customerId },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const modulesResult = await fetchWithBQ(
          modulesByGroupQuery({ moduleGroupHandle: INTERPRETERS_MODULE_GROUP }),
        );
        if (modulesResult.error) {
          return { error: modulesResult.error };
        }
        const { modules } = (modulesResult.data as CdxModulesListResponse).data;

        const owned: OwnedInstance[] = [];
        for (const module of modules) {
          const listResult = await fetchWithBQ(
            moduleInstancesQuery({ handle: module.handle, customerId }),
          );
          if (listResult.error) {
            return { error: listResult.error };
          }
          (
            listResult.data as CdxModuleInstancesListResponse
          ).data.module_instances.forEach(instance => {
            owned.push({ module, instance });
          });
        }

        const data: CdxInterpreterInstance[] = [];
        for (const { module, instance } of owned) {
          const detailResult = await fetchWithBQ(
            moduleInstanceQuery({
              handle: module.handle,
              instanceId: instance.id,
            }),
          );
          if (detailResult.error) {
            return { error: detailResult.error };
          }
          data.push({
            module,
            instance: (
              detailResult.data as CdxProxyEnvelope<CdxModuleInstanceWithChannels>
            ).data,
          });
        }

        return { data };
      },
      providesTags: [Tags.CdxModules, Tags.CdxModuleInstances],
    }),
  }),
});

export const { useListInterpreterInstancesQuery } = cdxInterpreterInstancesApi;
